import { PaginationControls } from '@common/types/pagination';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    DotsHorizontalIcon,
    DoubleArrowLeftIcon,
    DoubleArrowRightIcon,
} from '@radix-ui/react-icons';
import { Button, Flex, IconButton, Select, Text } from '@radix-ui/themes';
import { PropsWithChildren, useMemo } from 'react';
import { Trans } from 'react-i18next';

interface PaginationControlProps extends PaginationControls {
    isPaginated?: boolean;
}

export type PaginationItem =
    | { type: 'page'; value: number }
    | { type: 'ellipsis'; key: string };

// eslint-disable-next-line react-refresh/only-export-components
export const generatePagination = (
    pageIndex: number,
    pageCount: number,
    range: number = 1,
): PaginationItem[] => {
    // if (pageCount <= 1) return [];

    return Array.from({ length: pageCount }, (_, i) => i)
        .filter(
            (page) =>
                page === 0 ||
                page === pageCount - 1 ||
                Math.abs(page - pageIndex) <= range,
        )
        .reduce<PaginationItem[]>((acc, page, index, array) => {
            const pageItem: PaginationItem = { type: 'page', value: page };

            if (index === 0) return [pageItem];

            const prevPage = array[index - 1];
            const gap = page - prevPage;

            if (gap === 2) {
                return [
                    ...acc,
                    { type: 'page', value: prevPage + 1 },
                    pageItem,
                ];
            }

            if (gap > 2) {
                return [
                    ...acc,
                    { type: 'ellipsis', key: `ellipsis-${prevPage}-${page}` },
                    pageItem,
                ];
            }

            return [...acc, pageItem];
        }, []);
};

interface NavButtonProps extends PropsWithChildren {
    onClick: () => void;
    disabled: boolean;
}

const NavButton = ({ children, ...props }: NavButtonProps) => (
    <IconButton variant='ghost' color='gray' {...props}>
        {children}
    </IconButton>
);

export const PaginationControl = ({
    isPaginated = true,
    itemCount: itemsCount,
    hasPreviousPage,
    pageIndex,
    hasNextPage,
    pageSize,
    pageCount,
    pageSizes,
    setPageSize,
    previousPage,
    nextPage,
    setPageIndex,
}: PaginationControlProps) => {
    const pageRange = useMemo(
        () => generatePagination(pageIndex, pageCount),
        [pageIndex, pageCount],
    );

    if (!isPaginated /* || pageCount <= 1 */) return null;

    const startItem = itemsCount === 0 ? 0 : pageIndex * pageSize + 1;
    const endItem = Math.min((pageIndex + 1) * pageSize, itemsCount);

    return (
        <Flex
            gap='4'
            p='4'
            align='center'
            justify='between'
            wrap='wrap'
            width={'100%'}
        >
            <Flex gap='2' align='center'>
                <NavButton
                    onClick={() => setPageIndex(0)}
                    disabled={!hasPreviousPage}
                >
                    <DoubleArrowLeftIcon />
                </NavButton>

                <NavButton onClick={previousPage} disabled={!hasPreviousPage}>
                    <ChevronLeftIcon />
                </NavButton>

                <Flex gap='2' align='center'>
                    {pageRange.map((item) => {
                        if (item.type === 'ellipsis') {
                            return (
                                <DotsHorizontalIcon
                                    key={item.key}
                                    color='gray'
                                />
                            );
                        }

                        const pageNum = item.value;
                        const isActive = pageIndex === pageNum;

                        return (
                            <Button
                                key={pageNum}
                                variant={'ghost'}
                                color={isActive ? undefined : 'gray'}
                                onClick={() => setPageIndex(pageNum)}
                            >
                                <Text weight={isActive ? 'bold' : 'regular'}>
                                    {pageNum + 1}
                                </Text>
                            </Button>
                        );
                    })}
                </Flex>

                <NavButton onClick={nextPage} disabled={!hasNextPage}>
                    <ChevronRightIcon />
                </NavButton>

                <NavButton
                    onClick={() => setPageIndex(pageCount - 1)}
                    disabled={!hasNextPage}
                >
                    <DoubleArrowRightIcon />
                </NavButton>
            </Flex>

            <Flex gap='4' align='center'>
                <Text size='2' color='gray'>
                    <Trans
                        i18nKey='common:pagination.results_range'
                        values={{
                            start: startItem,
                            end: endItem,
                            total: itemsCount,
                        }}
                    />
                </Text>

                <Flex gap={'2'}>
                    <Text size='2' color='gray'>
                        <Trans i18nKey='common:pagination.itemsPerPage' />
                    </Text>

                    <Select.Root
                        value={pageSize.toString()}
                        onValueChange={(value) => setPageSize(Number(value))}
                    >
                        <Select.Trigger variant='ghost' color='gray' />
                        <Select.Content position='popper'>
                            {pageSizes.map((size) => (
                                <Select.Item key={size} value={`${size}`}>
                                    {size}
                                </Select.Item>
                            ))}
                        </Select.Content>
                    </Select.Root>
                </Flex>
            </Flex>
        </Flex>
    );
};
